import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, apiClient } from '../contexts/AuthContext';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  User, LogOut, Settings, Shield, Scale, Activity, TrendingUp, TrendingDown,
  ChevronDown, ChevronUp, Droplet, Flame, Dumbbell, Download, BarChart3,
  Phone, Mail, Cake, Save, Pencil, Ruler, Target
} from 'lucide-react';
import { toast } from 'sonner';
import { ProfilePhotoUpload, UserAvatarWithCard } from '../components/UserAvatar';
import { LineChart, Line, ResponsiveContainer, Tooltip, YAxis } from 'recharts';

export default function ProfilPage() {
  const { user, logout } = useAuth();

  // Parse YYYY-MM-DD without timezone shift
  const formatBirthDate = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };
  const [latestMetrics, setLatestMetrics] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingInfo, setEditingInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({ birth_date: '', phone: '', email: '', height: '', gender: '', activity_level: '', nutrition_goal: '' });
  const [savingInfo, setSavingInfo] = useState(false);
  const [tdeeHistory, setTdeeHistory] = useState([]);
  const [daySummary, setDaySummary] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [metricsRes, profileRes, summaryRes] = await Promise.allSettled([
        apiClient.get('/metrics/latest'),
        apiClient.get('/user/profile'),
        apiClient.get('/summary/daily'),
      ]);
      if (metricsRes.status === 'fulfilled') setLatestMetrics(metricsRes.value.data);
      if (summaryRes.status === 'fulfilled') setDaySummary(summaryRes.value.data);
      if (profileRes.status === 'fulfilled') {
        const p = profileRes.value.data;
        setProfile(p);
        setInfoForm({ birth_date: p.birth_date || '', phone: p.phone || '', email: p.email || '', height: p.height || '', gender: p.gender || '', activity_level: p.activity_level || '', nutrition_goal: p.nutrition_goal || '' });
      }
      // Fetch TDEE history for sparkline
      try {
        const { data: hist } = await apiClient.get('/metrics/tdee-history?limit=20');
        setTdeeHistory(hist);
      } catch {}
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = async () => { await logout(); };

  const handleSaveInfo = async () => {
    setSavingInfo(true);
    try {
      const payload = {};
      if (infoForm.birth_date) payload.birth_date = infoForm.birth_date;
      if (infoForm.phone) payload.phone = infoForm.phone;
      if (infoForm.email) payload.email = infoForm.email;
      if (infoForm.height) payload.height = parseFloat(infoForm.height);
      if (infoForm.gender) payload.gender = infoForm.gender;
      if (infoForm.activity_level) payload.activity_level = infoForm.activity_level;
      if (infoForm.nutrition_goal) payload.nutrition_goal = infoForm.nutrition_goal;
      const { data } = await apiClient.put('/user/profile', payload);
      // If key fields are now filled, mark onboarding as complete
      if (data.height && data.gender && data.activity_level && data.nutrition_goal && !data.onboarding_complete) {
        await apiClient.post('/user/onboarding', {
          height: data.height, gender: data.gender,
          activity_level: data.activity_level, nutrition_goal: data.nutrition_goal,
        }).catch(() => {});
      }
      toast.success('Profil mis a jour ! Tes besoins caloriques ont ete recalcules.');
      setEditingInfo(false);
      fetchData();
    } catch { toast.error('Erreur'); }
    finally { setSavingInfo(false); }
  };

  const handleExport = async (type, format = 'csv') => {
    try {
      let url;
      const isPdf = format === 'pdf';
      if (type === 'members') url = `/export/members-${format}`;
      else if (type === 'metrics') url = `/export/metrics-${format}/${user?.id}`;
      else if (type === 'challenges') url = '/export/challenges-csv';
      const { data } = await apiClient.get(url, { responseType: 'blob' });
      const mimeType = isPdf ? 'application/pdf' : 'text/csv';
      const ext = isPdf ? 'pdf' : 'csv';
      const blob = new Blob([data], { type: mimeType });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${type}_lameute.${ext}`;
      a.click();
      toast.success('Export telecharge !');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur export');
    }
  };

  const m = latestMetrics || {};
  const age = profile?.age || (profile?.birth_date ? (new Date().getFullYear() - parseInt(profile.birth_date.split('-')[0])) : null);
  const tdee = daySummary?.macro_targets?.calories || profile?.latest_tdee || m.tdee;
  const pn = daySummary?.profile_nutrition;

  return (
    <div className="min-h-screen bg-[#0F0F0F] pb-40">
      {/* Header */}
      <div className="bg-[#1B1B1B] border-b border-[#2A2A2E]">
        <div className="px-5 pt-8 pb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <ProfilePhotoUpload
                currentPhoto={profile?.profile_photo}
                onUploaded={(path) => { setProfile(p => ({ ...p, profile_photo: path })); fetchData(); }}
              />
              <div>
                <h1 className="text-3xl text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }} data-testid="profil-name">
                  {user?.name || 'Utilisateur'}
                </h1>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-zinc-400 capitalize">{user?.role || 'membre'}</p>
                  {age && <span className="text-xs text-[#FF6B35] bg-[#FF6B35]/10 px-1.5 py-0.5 rounded">{age} ans</span>}
                </div>
              </div>
            </div>
            <button onClick={handleLogout} data-testid="logout-button" className="w-10 h-10 rounded-full bg-zinc-800/50 flex items-center justify-center hover:bg-red-500/20 transition-colors">
              <LogOut className="w-5 h-5 text-zinc-400" />
            </button>
          </div>

          {/* Quick Stats Row */}
          {m.weight ? (
            <div className="grid grid-cols-3 gap-3" data-testid="profil-quick-stats">
              <StatBox label="Poids" value={m.weight} unit="kg" color="#FF6B35" />
              <StatBox label="Gras" value={m.body_fat} unit="%" color="#ef4444" />
              <StatBox label="Muscle" value={m.muscle_mass} unit="kg" color="#3b82f6" />
            </div>
          ) : (
            <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-4 text-center" data-testid="no-tanita-data">
              <Scale className="w-6 h-6 text-zinc-500 mx-auto mb-2" />
              <p className="text-sm text-[#CCCCCC]">Aucune donnee Tanita</p>
              <Link to="/metriques" className="text-xs text-[#FF6B35] font-semibold mt-1 inline-block">Ajouter mes metriques</Link>
            </div>
          )}
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* Profile incomplete alert */}
        {profile && (!profile.height || !profile.gender || !profile.activity_level || !profile.nutrition_goal) && (
          <div className="bg-[#FFB800]/15 border border-[#FFB800]/30 rounded-lg p-4" data-testid="profile-incomplete-alert">
            <p className="text-sm text-[#FFB800] font-bold mb-1">Profil incomplet</p>
            <p className="text-xs text-zinc-400 mb-3">Complete ton profil pour que tes besoins caloriques, tes macros et ton coach IA soient personnalises.</p>
            <button onClick={() => setEditingInfo(true)} className="text-xs bg-[#FFB800] text-black px-4 py-2 rounded-lg font-bold" data-testid="complete-profile-btn">
              Completer mon profil
            </button>
          </div>
        )}

        {/* Personal Info Section */}
        <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-5" data-testid="personal-info-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg text-white flex items-center gap-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              <User className="w-5 h-5 text-[#FF6B35]" /> Infos personnelles
            </h2>
            <button onClick={() => setEditingInfo(!editingInfo)} className="text-xs text-[#FF6B35] font-semibold flex items-center gap-1" data-testid="edit-info-btn">
              <Pencil className="w-3 h-3" /> {editingInfo ? 'Annuler' : 'Modifier'}
            </button>
          </div>

          {editingInfo ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs flex items-center gap-1"><Cake className="w-3 h-3" /> Date de naissance</Label>
                  <Input type="date" value={infoForm.birth_date} onChange={e => setInfoForm({...infoForm, birth_date: e.target.value})}
                    className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" data-testid="input-birth-date" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs flex items-center gap-1"><Ruler className="w-3 h-3" /> Taille (cm)</Label>
                  <Input type="number" step="1" min="100" max="250" value={infoForm.height} onChange={e => setInfoForm({...infoForm, height: e.target.value})}
                    placeholder="172" className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" data-testid="input-height" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs flex items-center gap-1"><User className="w-3 h-3" /> Sexe</Label>
                  <select value={infoForm.gender} onChange={e => setInfoForm({...infoForm, gender: e.target.value})}
                    className="w-full h-10 bg-[#0F0F0F] border border-[#2A2A2E] text-white rounded-md px-3 text-sm" data-testid="input-gender">
                    <option value="">—</option>
                    <option value="male">Homme</option>
                    <option value="female">Femme</option>
                  </select>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs flex items-center gap-1"><Activity className="w-3 h-3" /> Activite</Label>
                  <select value={infoForm.activity_level} onChange={e => setInfoForm({...infoForm, activity_level: e.target.value})}
                    className="w-full h-10 bg-[#0F0F0F] border border-[#2A2A2E] text-white rounded-md px-3 text-sm" data-testid="input-activity">
                    <option value="">—</option>
                    <option value="sedentaire">Sedentaire</option>
                    <option value="leger">Leger (1-2x/sem)</option>
                    <option value="modere">Modere (3-5x/sem)</option>
                    <option value="actif">Actif (6-7x/sem)</option>
                    <option value="tres_actif">Tres actif (2x/jour)</option>
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs flex items-center gap-1"><Target className="w-3 h-3" /> Objectif nutritionnel</Label>
                <select value={infoForm.nutrition_goal} onChange={e => setInfoForm({...infoForm, nutrition_goal: e.target.value})}
                  className="w-full h-10 bg-[#0F0F0F] border border-[#2A2A2E] text-white rounded-md px-3 text-sm" data-testid="input-goal">
                  <option value="">—</option>
                  <option value="perte">Perte de poids (-300 kcal)</option>
                  <option value="perte_gras">Perte de gras (-400 kcal, proteines elevees)</option>
                  <option value="seche">Seche (-500 kcal)</option>
                  <option value="maintien">Maintien</option>
                  <option value="prise">Prise de masse (+300 kcal)</option>
                </select>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs flex items-center gap-1"><Phone className="w-3 h-3" /> Telephone</Label>
                <Input type="tel" value={infoForm.phone} onChange={e => setInfoForm({...infoForm, phone: e.target.value})}
                  placeholder="89 12 34 56" className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" data-testid="input-phone" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs flex items-center gap-1"><Mail className="w-3 h-3" /> Email</Label>
                <Input type="email" value={infoForm.email} onChange={e => setInfoForm({...infoForm, email: e.target.value})}
                  placeholder="nom@mail.com" className="bg-[#0F0F0F] border-[#2A2A2E] text-white" data-testid="input-email" />
              </div>
              <Button onClick={handleSaveInfo} disabled={savingInfo} className="w-full bg-[#FF6B35] text-white hover:bg-[#FF8C55]" data-testid="save-info-btn">
                <Save className="w-4 h-4 mr-2" /> {savingInfo ? 'Enregistrement...' : 'Sauvegarder'}
              </Button>
            </div>
          ) : (
            <div className="space-y-2.5">
              <InfoRow icon={Cake} label="Date de naissance" value={profile?.birth_date ? `${formatBirthDate(profile.birth_date)}${age ? ` (${age} ans)` : ''}` : null} />
              <InfoRow icon={Ruler} label="Taille" value={profile?.height ? `${profile.height} cm` : null} />
              <InfoRow icon={User} label="Sexe" value={profile?.gender === 'male' ? 'Homme' : profile?.gender === 'female' ? 'Femme' : null} />
              <InfoRow icon={Activity} label="Activite" value={{sedentaire:'Sedentaire',leger:'Leger',modere:'Modere',actif:'Actif',tres_actif:'Tres actif'}[profile?.activity_level] || null} />
              <InfoRow icon={Target} label="Objectif" value={{perte:'Perte de poids',perte_gras:'Perte de gras',seche:'Seche',maintien:'Maintien',prise:'Prise de masse'}[profile?.nutrition_goal] || null} />
              <InfoRow icon={Phone} label="Telephone" value={profile?.phone} />
              <InfoRow icon={Mail} label="Email" value={profile?.email} />
            </div>
          )}
        </div>

        {/* Full Tanita Metrics */}
        {/* Besoins caloriques calculés */}
        {tdee && (
          <div className="bg-gradient-to-r from-[#FF6B35]/10 to-[#FFB800]/10 border border-[#FF6B35]/20 rounded-lg p-4" data-testid="tdee-banner">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-400 font-semibold">Besoins caloriques du jour (Mifflin-St Jeor)</p>
                <p className="font-mono text-2xl font-bold text-[#FF6B35]">{tdee} <span className="text-sm text-zinc-400 font-normal">kcal/jour</span></p>
              </div>
              <div className="text-right">
                {profile?.latest_weight && <p className="text-xs text-zinc-500">{profile.latest_weight} kg</p>}
                {profile?.height && <p className="text-xs text-zinc-500">{profile.height} cm</p>}
                {age && <p className="text-xs text-zinc-500">{age} ans</p>}
              </div>
            </div>
            <p className="text-[10px] text-zinc-500 mt-2">
              {pn ? (
                <>Repos (BMR × 1,2) {pn.repos} kcal + Sport {daySummary?.training_burned || 0} kcal {pn.kcal_adjust !== 0 && `${pn.kcal_adjust > 0 ? '+' : ''}${pn.kcal_adjust} (${{cut:'Seche',perte_gras:'Perte de gras',perte:'Perte de poids',bulk:'Prise de masse',recomp:'Maintien'}[pn.objective] || 'Objectif'})`}</>
              ) : (
                <>Calcule selon ton profil ({
                  {sedentaire:'Sedentaire',leger:'Leger',modere:'Modere',actif:'Actif',tres_actif:'Tres actif'}[profile?.activity_level] || 'Modere'
                } · {
                  {perte:'Deficit -300',perte_gras:'Perte de gras -400',seche:'Seche -500',maintien:'Maintien',prise:'Surplus +300'}[profile?.nutrition_goal] || 'Maintien'
                })</>
              )}
            </p>
            {tdeeHistory.length > 2 && (
              <div className="mt-3 -mx-1" data-testid="tdee-sparkline">
                <p className="text-[9px] text-zinc-600 mb-1">Evolution poids (derniers enregistrements)</p>
                <ResponsiveContainer width="100%" height={48}>
                  <LineChart data={tdeeHistory}>
                    <YAxis domain={['auto', 'auto']} hide />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1B1B1B', border: '1px solid #2A2A2E', borderRadius: '6px', fontSize: '11px' }}
                      labelFormatter={l => ''}
                      formatter={(v, name) => [`${v} ${name === 'weight' ? 'kg' : 'kcal'}`, name === 'weight' ? 'Poids' : 'TDEE']}
                    />
                    <Line type="monotone" dataKey="weight" stroke="#FF6B35" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            {tdeeHistory.length <= 2 && (
              <p className="text-[9px] text-zinc-600 mt-2 italic" data-testid="tdee-sparkline-hint">Enregistre 3 pesees pour voir ta courbe de progression ici.</p>
            )}
          </div>
        )}

        {m.weight && (
          <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-5" data-testid="tanita-metrics">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl text-white flex items-center gap-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                <Scale className="w-5 h-5 text-[#FF6B35]" /> Balance Tanita
              </h2>
              <Link to="/metriques" className="text-xs text-[#FF6B35] font-semibold hover:underline" data-testid="edit-metrics-link">
                Modifier
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Eau corporelle', value: m.body_water, unit: '%', icon: Droplet },
                { label: 'Masse osseuse', value: m.bone_mass, unit: 'kg', icon: Activity },
                { label: 'Graisse viscerale', value: m.visceral_fat, unit: '', icon: Flame },
                { label: 'BMR', value: m.bmr, unit: 'kcal', icon: TrendingUp },
                { label: 'Age metabolique', value: m.metabolic_age, unit: 'ans', icon: Activity },
                { label: 'TDEE', value: tdee || m.tdee, unit: 'kcal/j', icon: Dumbbell },
              ].filter(x => x.value).map(({ label, value, unit, icon: Icon }) => (
                <div key={label} className="flex items-center gap-3 p-3 bg-[#0F0F0F] rounded-md">
                  <Icon className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-zinc-500 truncate">{label}</p>
                    <p className="font-mono text-base font-bold text-white">{value} <span className="text-xs text-zinc-500 font-normal">{unit}</span></p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Links */}
        <div className="space-y-2">
          <Link to="/metriques" className="flex items-center gap-3 p-4 bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg hover:border-zinc-700 transition-colors" data-testid="profil-link-metriques">
            <Scale className="w-5 h-5 text-[#FF6B35]" />
            <span className="text-sm text-white font-medium flex-1">Historique metriques</span>
            <ChevronDown className="w-4 h-4 text-zinc-500 -rotate-90" />
          </Link>

          <Link to="/performance" className="flex items-center gap-3 p-4 bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg hover:border-zinc-700 transition-colors" data-testid="profil-link-performance">
            <BarChart3 className="w-5 h-5 text-[#FFB800]" />
            <span className="text-sm text-white font-medium flex-1">Ma Performance</span>
            <ChevronDown className="w-4 h-4 text-zinc-500 -rotate-90" />
          </Link>

          {user?.role === 'admin' && (
            <Link to="/admin" className="flex items-center gap-3 p-4 bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg hover:border-[#FF6B35]/30 transition-colors" data-testid="profil-link-admin">
              <Shield className="w-5 h-5 text-[#FF6B35]" />
              <span className="text-sm text-white font-medium flex-1">Panel Admin</span>
              <ChevronDown className="w-4 h-4 text-zinc-500 -rotate-90" />
            </Link>
          )}

          {user?.role === 'admin' && (
            <Link to="/edit-programs" className="flex items-center gap-3 p-4 bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg hover:border-zinc-700 transition-colors" data-testid="profil-link-programs">
              <Settings className="w-5 h-5 text-zinc-400" />
              <span className="text-sm text-white font-medium flex-1">Editer les programmes</span>
              <ChevronDown className="w-4 h-4 text-zinc-500 -rotate-90" />
            </Link>
          )}
        </div>

        {/* Export Section */}
        <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-5" data-testid="export-section">
          <h2 className="text-lg text-white flex items-center gap-2 mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            <Download className="w-5 h-5 text-[#FF6B35]" /> Exporter
          </h2>
          <div className="flex gap-2 flex-wrap">
            <ExportBtn label="Metriques CSV" onClick={() => handleExport('metrics', 'csv')} testId="export-metrics-csv-btn" />
            <ExportBtn label="Metriques PDF" onClick={() => handleExport('metrics', 'pdf')} testId="export-metrics-pdf-btn" variant="danger" />
            {user?.role === 'admin' && (
              <>
                <ExportBtn label="Membres CSV" onClick={() => handleExport('members', 'csv')} testId="export-members-csv-btn" />
                <ExportBtn label="Membres PDF" onClick={() => handleExport('members', 'pdf')} testId="export-members-pdf-btn" variant="danger" />
              </>
            )}
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 p-2.5 bg-[#0F0F0F] rounded-md">
      <Icon className="w-4 h-4 text-zinc-500 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] text-zinc-500">{label}</p>
        <p className={`text-sm font-medium ${value ? 'text-white' : 'text-zinc-600 italic'}`}>{value || 'Non renseigne'}</p>
      </div>
    </div>
  );
}

function StatBox({ label, value, unit, color }) {
  return (
    <div className="bg-[#0F0F0F] rounded-lg p-3 text-center" style={{ borderTop: `2px solid ${color}` }}>
      <p className="font-mono text-2xl font-bold text-white">{value || '—'}</p>
      <p className="text-xs text-zinc-500 mt-0.5">{label} ({unit})</p>
    </div>
  );
}

function ExportBtn({ label, onClick, testId, variant }) {
  const isDanger = variant === 'danger';
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${
        isDanger
          ? 'bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20'
          : 'bg-zinc-800/50 border border-zinc-700 text-zinc-300 hover:bg-zinc-800'
      }`}
    >
      {label}
    </button>
  );
}
